import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  IInputMaskOptions,
  InputMask
} from '@hse-design/core/lib/es/inputMask';
import { loadInputMask } from '@hse-design/core';

import { IntrinsicPropsWithoutRef } from '../../common/types';

export interface UseInputMaskArgs {
  mask?: IInputMaskOptions['maskOptions'];
  value?: string;
  onInput?: IntrinsicPropsWithoutRef<'input'>['onInput'];
  onUnmaskedChange?: (value: string | undefined) => void;
  inputRef: MutableRefObject<HTMLInputElement | null>;
}

export function useInputMask({
  mask,
  inputRef,
  onInput: onInputHandler,
  onUnmaskedChange
}: UseInputMaskArgs) {
  const [maskState, setMaskState] = useState<{ type: typeof InputMask } | null>(
    null
  );
  const [inputMask, setInputMask] = useState<InputMask | null>(null);

  useEffect(() => {
    if (!mask) return;
    if (maskState) return;

    let isActive = true;
    loadInputMask().then((InputMask) => {
      if (!isActive) return;
      setMaskState({ type: InputMask });
    });

    return () => {
      isActive = false;
    };
  }, [mask, maskState]);

  useEffect(() => {
    if (!maskState) {
      setInputMask(null);
      return;
    }

    const effectMaskRef = new maskState.type({
      $inputElement: inputRef.current
    });

    effectMaskRef.onDidMount();
    setInputMask(effectMaskRef);

    return () => {
      effectMaskRef.onWillUnmount();
    };
  }, [maskState]);

  useEffect(() => {
    if (mask && inputMask) {
      inputMask.onUpdate({
        $inputElement: inputRef.current as HTMLInputElement,
        maskOptions: mask
      });
    }
  }, [mask, inputMask]);

  const onInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (inputMask) {
        onUnmaskedChange?.(inputMask.getUnmaskedValue());
      }
      onInputHandler?.(e);
    },
    [onUnmaskedChange, onInputHandler, inputMask]
  );

  return {
    onInput
  };
}
